import { useState, useEffect } from "react";
import {
  Modal,
  Tabs,
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  notification,
  Spin,
  Upload,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { httpPost, httpGet, customBaseUrl, httpPostFormData } from "../../../../store/http";
import { showLoader, hideLoader } from "../../../../utils/loader";

const { TabPane } = Tabs;
const { Option } = Select;

const AdminTransfer = ({
  userId,
  userWallets,
  visible,
  onClose,
  onTransferSuccess,
}) => {
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [fetchedWallets, setFetchedWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("same-bank");
  const [form] = Form.useForm();
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [verifyingSameBankAccount, setVerifyingSameBankAccount] = useState(false);
  const [intraFile, setIntraFile] = useState(null);
  const [interFile, setInterFile] = useState(null);

  const activeWallets = userWallets?.length > 0 ? userWallets : fetchedWallets;

  useEffect(() => {
    const fetchBanks = async () => {
      setLoadingBanks(true);
      try {
        const res = await httpGet(
          `${customBaseUrl.tranv2}/api/v1/transactions/commercial-banks`,
        );
        if (res.success || res.status === true) {
          setBanks(res.data || []);
        } else {
          notification.error({ message: "Failed to fetch banks" });
        }
      } catch (error) {
        notification.error({ message: "Failed to fetch banks" });
      } finally {
        setLoadingBanks(false);
      }
    };

    const fetchWallets = async () => {
      if (!userId) return;
      setLoadingWallets(true);
      try {
        const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/accounts/user/${userId}/details`);
        if (res?.success && Array.isArray(res.data?.accounts)) {
          setFetchedWallets(res.data.accounts);
        }
      } catch (err) {
        console.error("Failed to fetch user wallets", err);
      } finally {
        setLoadingWallets(false);
      }
    };

    if (visible) {
      if (!userWallets || userWallets.length === 0) {
        fetchWallets();
      }
      if (activeTab === "external-bank") {
        fetchBanks();
      }
    }
  }, [visible, activeTab, userId, userWallets]);

  const handlePayerAccountChange = (accountNumber) => {
    const selectedWallet = activeWallets.find(
      (wallet) => wallet.accountNumber === accountNumber,
    );
    if (selectedWallet) {
      form.setFieldsValue({ payer: selectedWallet.accountName });
    }
  };

  const handleVerifySameBankAccount = async (e) => {
    const accountNumber = e.target.value;
    if (accountNumber && accountNumber.length === 10) {
      setVerifyingSameBankAccount(true);
      try {
        const res = await httpGet(
          `${customBaseUrl.authUrl}/api/v1/accounts/account/${accountNumber}/enquiry`,
        );
        if (res.success || res.status === true) {
          const accountName = res.data?.accountName;
          if (accountName) {
            form.setFieldsValue({ toAccountName: accountName });
            notification.success({ message: "Account verified successfully!" });
          } else {
            form.setFieldsValue({ toAccountName: "" });
            notification.warning({
              message: "Could not retrieve account name.",
            });
          }
        } else {
          form.setFieldsValue({ toAccountName: "" });
          notification.error({
            message: res.message || "Failed to verify account.",
          });
        }
      } catch (error) {
        form.setFieldsValue({ toAccountName: "" });
        notification.error({
          message: error.message || "An error occurred during verification.",
        });
      } finally {
        setVerifyingSameBankAccount(false);
      }
    } else {
      form.setFieldsValue({ toAccountName: "" });
    }
  };

  const handleVerifyAccount = async () => {
    const accountNumber = form.getFieldValue("receiverAccountNumber");
    const bankCode = form.getFieldValue("receiverBankCode");

    if (!accountNumber || !bankCode) {
      notification.error({
        message: "Please select a bank and enter an account number first.",
      });
      return;
    }

    setVerifyingAccount(true);
    try {
      const payload = { accountNumber, bankCode };
      const res = await httpPost(
        `${customBaseUrl.tranv2}/api/v1/transactions/name-enquiry`,
        payload,
      );

      if (res.success || res.status === true) {
        if (res.data && res.data.successful) {
          const accountName = res.data.name;
          if (accountName) {
            form.setFieldsValue({ receiverName: accountName });
            notification.success({ message: "Account verified successfully!" });
          } else {
            notification.warning({
              message: "Could not retrieve account name.",
            });
            form.setFieldsValue({ receiverName: "" });
          }
        } else {
          notification.error({
            message: res.data?.responseMessage || "Failed to verify account.",
          });
          form.setFieldsValue({ receiverName: "" });
        }
      } else {
        notification.error({
          message: res.message || "Failed to verify account.",
        });
        form.setFieldsValue({ receiverName: "" });
      }
    } catch (error) {
      notification.error({
        message: error.message || "An error occurred during verification.",
      });
      form.setFieldsValue({ receiverName: "" });
    } finally {
      setVerifyingAccount(false);
    }
  };

  const downloadTemplate = (type) => {
    const headers =
      type === "intra"
        ? "Account Number,Amount,Narration"
        : "Bank Code,Account Number,Amount,Narration";
    const blob = new Blob([headers], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `bulk_${type}_transfer_template.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFinish = async (values) => {
    setSubmitting(true);
    showLoader();

    const isSameBank = activeTab === "same-bank";
    const isSelfTransfer = activeTab === "transfer-to-self";
    const isBulkIntra = activeTab === "bulk-intra";
    const isBulkInter = activeTab === "bulk-inter";

    let endpoint = "";
    if (isSameBank || isSelfTransfer) {
      endpoint = "/api/v1/admin/maker-checker/maker/intra-bank-transfer";
    } else if (isBulkIntra) {
      endpoint = "/api/v1/admin/maker-checker/maker/bulk-intra-bank-transfer";
    } else if (isBulkInter) {
      endpoint = "/api/v1/admin/maker-checker/maker/bulk-inter-bank-transfer";
    } else {
      endpoint = "/api/v1/admin/maker-checker/maker/inter-bank-transfer";
    }

    const reference = `REF-${Date.now()}`;

    let response;

    if (isBulkIntra || isBulkInter) {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("fromAccountNumber", values.fromAccountNumber);
      if (values.narration) formData.append("narration", values.narration);
      formData.append("makerReason", values.makerReason);
      formData.append("expiryHours", Number(values.expiryHours) || 24);
      formData.append("reference", reference);
      formData.append("file", isBulkIntra ? intraFile : interFile);

      response = await httpPostFormData(`${customBaseUrl.tranv2}${endpoint}`, formData);
    } else {
      let payload = {
        userId,
        amount: Number(values.amount),
        narration: values.narration,
        makerReason: values.makerReason,
        expiryHours: Number(values.expiryHours) || 24,
        reference,
      };

      if (isSameBank || isSelfTransfer) {
        const toAccountName = isSelfTransfer
          ? activeWallets.find(w => w.accountNumber === values.toAccountNumber)?.accountName
          : values.toAccountName;

        payload = {
          ...payload,
          fromAccountNumber: values.fromAccountNumber,
          toAccountNumber: values.toAccountNumber,
          toAccountName: toAccountName,
        };
      } else {
        payload = {
          ...payload,
          payerAccountNumber: values?.payerAccountNumber,
          payer: values?.payer,
          receiverBankCode: values?.receiverBankCode,
          receiverAccountNumber: values?.receiverAccountNumber,
          receiverName: values?.receiverName,
        };
      }
      response = await httpPost(`${customBaseUrl.tranv2}${endpoint}`, payload);
    }

    try {
      const res = response;
      if (res.success || res.status === true) {
        notification.success({ message: "Transfer initiated successfully!" });
        form.resetFields();
        if (onTransferSuccess) {
          onTransferSuccess();
        }
      } else {
        console.error("Transfer initiation failed:", res);
        notification.error({
          message:
            res.message || res.data?.message || "Transfer initiation failed.",
          description: res.data
            ? JSON.stringify(res.data)
            : "No additional details available.",
        });
      }
    } catch (error) {
      notification.error({ message: error.message || "An error occurred." });
    } finally {
      setSubmitting(false);
      hideLoader();
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      title="Make a Transfer on Behalf of User"
      destroyOnClose
      width={600}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          form.resetFields();
          setIntraFile(null);
          setInterFile(null);
        }}
      >
        <TabPane tab="Same Bank" key="same-bank">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            onFinishFailed={(errorInfo) => {
              console.error("Form submission failed:", errorInfo);
              notification.error({
                message: "Validation Error",
                description: "Please check all form fields for errors.",
              });
            }}
            initialValues={{ expiryHours: 24 }}
          >
            <Form.Item
              name="fromAccountNumber"
              label="From Account"
              rules={[
                {
                  required: activeTab === "same-bank",
                  message: "Please select a source account.",
                },
              ]}
            >
              <Select placeholder="Select source account" loading={loadingWallets}>
                {activeWallets?.map((wallet) => (
                  <Option
                    key={wallet.accountNumber}
                    value={wallet.accountNumber}
                  >
                    {wallet.accountName} ({wallet.accountNumber})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="toAccountNumber"
              label="To Account Number"
              rules={[
                {
                  required: activeTab === "same-bank",
                  message: "Please enter a destination account number.",
                },
              ]}
            >
              <Input
                onChange={handleVerifySameBankAccount}
                maxLength={10}
                suffix={verifyingSameBankAccount ? <Spin size="small" /> : null}
              />
            </Form.Item>
            <Form.Item
              name="toAccountName"
              label="To Account Name"
              rules={[
                {
                  required: activeTab === "same-bank",
                  message: "Account name is required.",
                },
              ]}
            >
              <Input placeholder="Account name" disabled />
            </Form.Item>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="narration"
              label="Narration"
              rules={[{ required: true }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item
              name="makerReason"
              label="Reason for Transfer"
              rules={[{ required: true }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="expiryHours" label="Expiry Hours">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Initiate Transfer
              </Button>
            </Form.Item>
          </Form>
        </TabPane>
        <TabPane tab="External Bank" key="external-bank">
          <Spin spinning={loadingBanks}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              onFinishFailed={(errorInfo) => {
                console.error("Form submission failed:", errorInfo);
                notification.error({
                  message: "Validation Error",
                  description: "Please check all form fields for errors.",
                });
              }}
              initialValues={{ expiryHours: 24 }}
            >
              <Form.Item
                name="payerAccountNumber"
                label="From Account"
                rules={[
                  {
                    required: activeTab === "external-bank",
                    message: "Please select a source account.",
                  },
                ]}
              >
                <Select
                  placeholder="Select source account"
                  onChange={handlePayerAccountChange}
                  loading={loadingWallets}
                >
                  {activeWallets?.map((wallet) => (
                    <Option
                      key={wallet.accountNumber}
                      value={wallet.accountNumber}
                    >
                      {wallet.accountName} ({wallet.accountNumber})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="payer"
                label="Payer Name"
                rules={[
                  {
                    required: activeTab === "external-bank",
                    message: "Payer name is required.",
                  },
                ]}
              >
                <Input disabled placeholder="Payer name (auto-filled)" />
              </Form.Item>
              <Form.Item
                name="receiverBankCode"
                label="Receiver Bank"
                rules={[
                  {
                    required: activeTab === "external-bank",
                    message: "Please select a receiver bank.",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select bank"
                  optionFilterProp="children"
                >
                  {banks.map((bank) => (
                    <Option key={bank.Code} value={bank.Code}>
                      {bank.Name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Receiver Account Number">
                <Input.Group compact>
                  <Form.Item
                    name="receiverAccountNumber"
                    noStyle
                    rules={[
                      {
                        required: activeTab === "external-bank",
                        message: "Account number is required",
                      },
                    ]}
                  >
                    <Input
                      style={{ width: "calc(100% - 100px)" }}
                      placeholder="Receiver Account Number"
                    />
                  </Form.Item>
                  <Button
                    style={{ width: "100px" }}
                    onClick={handleVerifyAccount}
                    loading={verifyingAccount}
                  >
                    Verify
                  </Button>
                </Input.Group>
              </Form.Item>
              <Form.Item
                name="receiverName"
                label="Receiver Name"
                rules={[
                  {
                    required: activeTab === "external-bank",
                    message: "Receiver name is required.",
                  },
                ]}
              >
                <Input
                  placeholder="Receiver Name (auto-filled on verification)"
                  disabled
                />
              </Form.Item>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="narration"
                label="Narration"
                rules={[{ required: true }]}
              >
                <Input.TextArea />
              </Form.Item>
              <Form.Item
                name="makerReason"
                label="Reason for Transfer"
                rules={[{ required: true }]}
              >
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="expiryHours" label="Expiry Hours">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Initiate Transfer
                </Button>
              </Form.Item>
            </Form>
          </Spin>
        </TabPane>
        <TabPane tab="Transfer to Self" key="transfer-to-self">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{ expiryHours: 24 }}
          >
            <Form.Item
              name="fromAccountNumber"
              label="Source Account"
              rules={[{ required: true, message: "Please select a source account." }]}
            >
              <Select placeholder="Select source account" loading={loadingWallets}>
                {activeWallets?.map((wallet) => (
                  <Option key={wallet.accountNumber} value={wallet.accountNumber}>
                    {wallet.accountName} ({wallet.accountNumber})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="toAccountNumber"
              label="Destination Account (Own Wallet)"
              rules={[
                { required: true, message: "Please select a destination account." },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('fromAccountNumber') !== value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Source and destination accounts must be different.'));
                  },
                }),
              ]}
            >
              <Select placeholder="Select destination account" loading={loadingWallets}>
                {activeWallets?.map((wallet) => (
                  <Option key={wallet.accountNumber} value={wallet.accountNumber}>
                    {wallet.accountName} ({wallet.accountNumber})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="narration"
              label="Narration"
              rules={[{ required: true }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item
              name="makerReason"
              label="Reason for Transfer"
              rules={[{ required: true }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="expiryHours" label="Expiry Hours">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Initiate Self-Transfer
              </Button>
            </Form.Item>
          </Form>
        </TabPane>
        {/* <TabPane tab="Bulk Same Bank (Excel)" key="bulk-intra">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{ expiryHours: 24 }}
          >
            <Form.Item
              name="fromAccountNumber"
              label="Source Account"
              rules={[{ required: true, message: "Please select a source account." }]}
            >
              <Select placeholder="Select source account">
                {userWallets?.map((wallet) => (
                  <Option key={wallet.accountNumber} value={wallet.accountNumber}>
                    {wallet.accountName} ({wallet.accountNumber})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Excel File"
              required
              extra={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Columns: Account Number, Amount, Narration</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadTemplate('intra')}
                  >
                    Download Template
                  </Button>
                </div>
              }
            >
              <Upload
                beforeUpload={(file) => {
                  setIntraFile(file);
                  return false;
                }}
                onRemove={() => setIntraFile(null)}
                fileList={intraFile ? [intraFile] : []}
                accept=".xlsx, .xls, .csv"
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              name="makerReason"
              label="Reason for Bulk Transfer"
              rules={[{ required: true }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="expiryHours" label="Expiry Hours">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Initiate Bulk Transfer
              </Button>
            </Form.Item>
          </Form>
        </TabPane> */}
        {/* <TabPane tab="Bulk External Bank (Excel)" key="bulk-inter">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{ expiryHours: 24 }}
          >
            <Form.Item
              name="fromAccountNumber"
              label="Source Account"
              rules={[{ required: true, message: "Please select a source account." }]}
            >
              <Select placeholder="Select source account">
                {userWallets?.map((wallet) => (
                  <Option key={wallet.accountNumber} value={wallet.accountNumber}>
                    {wallet.accountName} ({wallet.accountNumber})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Excel File"
              required
              extra={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Columns: Bank Code, Account Number, Amount, Narration</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadTemplate('inter')}
                  >
                    Download Template
                  </Button>
                </div>
              }
            >
              <Upload
                beforeUpload={(file) => {
                  setInterFile(file);
                  return false;
                }}
                onRemove={() => setInterFile(null)}
                fileList={interFile ? [interFile] : []}
                accept=".xlsx, .xls, .csv"
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              name="makerReason"
              label="Reason for Bulk Transfer"
              rules={[{ required: true }]}
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="expiryHours" label="Expiry Hours">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Initiate Bulk External Transfer
              </Button>
            </Form.Item>
          </Form>
        </TabPane> */}
      </Tabs>
    </Modal>
  );
};

export default AdminTransfer;
